import React, { useState, useRef, useEffect } from 'react';
import { 
  Card, 
  Upload, 
  Button, 
  message, 
  Spin, 
  Tabs, 
  Form, 
  Input, 
  Slider,
  Switch,
  Row,
  Col,
  Divider,
  Select,
  Radio,
  Typography,
  Space,
  Modal,
  Image,
  Tooltip
} from 'antd';
import { 
  PictureOutlined, 
  UploadOutlined, 
  DeleteOutlined, 
  SettingOutlined,
  SwapOutlined,
  SaveOutlined,
  EditOutlined,
  EyeOutlined,
  ExperimentOutlined,
  PlusOutlined,
  AppstoreAddOutlined,
  CheckOutlined,
  UserOutlined
} from '@ant-design/icons';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { getConfig } from '../services/api';
import { 
  loadImage, 
  addWatermark, 
  generateGrid, 
  uploadImage, 
  generateModelImage,
  createCanvas 
} from '../services/imageService';
import { getModelHistory, saveModelHistory, deleteModelHistory } from '../services/modelHistoryService';
import { createPattern, getPatternCategories } from '../services/patternService';


const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

// 图片压缩函数
const compressImage = async (file) => {
  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true
  };
  
  try {
    return await imageCompression(file, options);
  } catch (error) {
    console.error('图片压缩失败:', error);
    return file;
  }
};

const SampleProcessor = () => {
  // State管理
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modelImage, setModelImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processMode, setProcessMode] = useState('auto'); // 'auto' 或 'manual'
  const [nineGridImage, setNineGridImage] = useState(null);
  const [activeTab, setActiveTab] = useState('upload');
  
  // 模特图片状态
  const [modelTemplates, setModelTemplates] = useState([]);
  const [selectedModelTemplate, setSelectedModelTemplate] = useState(null);
  const [customModelImage, setCustomModelImage] = useState(null);
  
  // 提示词模板状态
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [currentPromptTemplate, setCurrentPromptTemplate] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  
  // 水印设置
  const [watermarkSettings, setWatermarkSettings] = useState({
    enabled: true,
    text: '样品展示',
    fontSize: 36,
    opacity: 0.3,
    color: 'rgba(255, 255, 255, 0.8)',
    position: 'center', // center, topLeft, topRight, bottomLeft, bottomRight
    angle: -45,
    repeat: true,
  });
  
  // 图片占位符
  const [fallbackImageUrl, setFallbackImageUrl] = useState('/err.svg');
  
  // 图片加载错误处理函数
  const handleImageError = (e) => {
    console.log('图片加载失败，使用占位图', e.target.src);
    e.target.onerror = null; // 防止循环触发错误
    e.target.src = fallbackImageUrl;
    // 调整SVG占位图的展示比例
    e.target.style.objectFit = 'contain';
    e.target.style.padding = '10px';
    e.target.style.background = '#f5f5f5';
  };
  
  // 新增模特预览功能相关状态
  const [modelPreviewVisible, setModelPreviewVisible] = useState(false);
  const [currentModelImage, setCurrentModelImage] = useState('');
  
  // 模特历史记录
  const [modelHistory, setModelHistory] = useState([]);
  const [modelHistoryVisible, setModelHistoryVisible] = useState(false);
  const [selectedHistoryModel, setSelectedHistoryModel] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // 样板添加相关状态
  const [patternCategories, setPatternCategories] = useState([]);
  const [addToPatternVisible, setAddToPatternVisible] = useState(false);
  const [patternFormData, setPatternFormData] = useState({
    name: '',
    categoryId: '',
    description: ''
  });
  
  // 模特筛选相关状态
  const [modelCategoryFilter, setModelCategoryFilter] = useState('all'); // 'all', '亚洲人', '欧洲人', '非洲人'
  const [modelAgeFilter, setModelAgeFilter] = useState('all'); // 'all', '青年', '中年', '老年'
  
  // 上传组件引用
  const uploadRef = useRef(null);
  
  // 处理图片排序
  const handleImageSort = (oldIndex, newIndex) => {
    const newImageList = [...images];
    const [movedItem] = newImageList.splice(oldIndex, 1);
    newImageList.splice(newIndex, 0, movedItem);
    setImages(newImageList);
  };
  
  // 处理样板表单输入变更
  const handlePatternInputChange = (e) => {
    const { name, value } = e.target;
    setPatternFormData({ ...patternFormData, [name]: value });
  };

  // 保存提示词模板
  const savePromptTemplate = (name, content) => {
    // 生成新的ID
    const newId = `template_${Date.now()}`;
    const newTemplate = { id: newId, name, content };
    
    // 添加到模板列表
    setPromptTemplates(prev => [...prev, newTemplate]);
    
    // 自动选择新模板
    setCurrentPromptTemplate(newId);
    setCustomPrompt(content);
    
    message.success(`已保存提示词模板: ${name}`);
  };
  
  // 处理模特图片上传
  const handleModelImageUpload = async (file) => {
    // 验证文件类型
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件!');
      return Upload.LIST_IGNORE;
    }
    
    // 验证文件大小
    if (file.size > 10 * 1024 * 1024) {
      message.error('图片大小不能超过10MB');
      return Upload.LIST_IGNORE;
    }
    
    try {
      // 压缩图片
      const compressedFile = await compressImage(file);
      
      // 创建图片对象
      const newImage = {
        uid: file.uid,
        name: file.name,
        status: 'done',
        originFileObj: compressedFile
      };
      
      // 生成预览URL
      newImage.url = URL.createObjectURL(compressedFile);
      
      // 更新自定义模特图片
      setCustomModelImage(newImage);
      setSelectedModelTemplate(null); // 清除已选模板
      message.success('模特图片上传成功');
      
      // 防止文件上传组件自动上传
      return false;
    } catch (error) {
      console.error('图片处理失败:', error);
      message.error('图片处理失败');
      return Upload.LIST_IGNORE;
    }
  };
  
  // 选择模特模板
  const handleSelectModelTemplate = (template) => {
    setSelectedModelTemplate(template);
    setCustomModelImage(null); // 清除自定义模特图片
    message.success(`已选择模特模板：${template.name}`);
  };
  
  // 选择提示词模板
  const handleSelectPromptTemplate = (templateId) => {
    setCurrentPromptTemplate(templateId);
    const selectedTemplate = promptTemplates.find(t => t.id === templateId);
    if (selectedTemplate) {
      setCustomPrompt(selectedTemplate.content);
      message.success(`已选择提示词模板：${selectedTemplate.name}`);
    }
  };
  
  // 加载样板分类
  const fetchPatternCategories = async () => {
    try {
      const response = await getPatternCategories();
      if (response.success) {
        setPatternCategories(response.data);
      } else {
        message.error('获取样板分类失败');
      }
    } catch (error) {
      console.error('获取样板分类错误', error);
    }
  };

  // 加载模特模板从 JSON 文件
  const loadModelTemplates = async () => {
    try {
      const response = await fetch('/images/templates/model-templates.json');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.templates && Array.isArray(data.templates)) {
        setModelTemplates(data.templates);
      } else {
        message.error('加载模特模板失败：数据格式错误');
      }
    } catch (error) {
      console.error('加载模特模板失败:', error);
      message.error('加载模特模板失败，请检查网络连接');
    }
  };

  // 初始化模特模板和加载模特历史记录
  useEffect(() => {
    // 加载样板分类
    fetchPatternCategories();
    
    // 加载模特模板
    loadModelTemplates();
    
    // 加载模特历史记录
    loadModelHistory();
  }, []);
  
  // 加载模特历史记录
  const loadModelHistory = async () => {
    try {
      setLoadingHistory(true);
      const historyData = await getModelHistory();
      
      // 将数据转换为前端需要的格式
      const formattedHistory = historyData.map(item => ({
        uid: item.id,
        url: item.url,
        createdAt: item.created_at,
        prompt: item.prompt,
        metadata: item.metadata ? JSON.parse(item.metadata) : null,
        isModelImage: true
      }));
      
      setModelHistory(formattedHistory);
    } catch (error) {
      console.error('加载模特历史记录失败:', error);
      message.error('加载模特历史记录失败');
    } finally {
      setLoadingHistory(false);
    }
  };

  // 图片上传处理
  const handleImageUpload = async (file) => {
    // 验证文件类型
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件!');
      return Upload.LIST_IGNORE;
    }
    
    // 验证文件大小
    if (file.size > 100 * 1024 * 1024) {
      message.error('图片大小不能超过100MB');
      return Upload.LIST_IGNORE;
    }
    
    // 验证文件格式
    const isJpgOrPng = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
    if (!isJpgOrPng) {
      message.error('只支持JPG、PNG和WebP格式的图片');
      return Upload.LIST_IGNORE;
    }
    
    // 验证图片数量上限
    if (images.length >= 8) {
      message.error('最多只能上传8张样品图片');
      return Upload.LIST_IGNORE;
    }
    
    // 压缩图片
    try {
      message.loading(`处理图片: ${file.name}`, 0.5);
      const compressedFile = await compressImage(file);
      
      // 显示压缩结果
      const beforeSize = (file.size / 1024 / 1024).toFixed(2);
      const afterSize = (compressedFile.size / 1024 / 1024).toFixed(2);
      message.success(`图片已压缩: ${beforeSize}MB → ${afterSize}MB`);
      
      // 生成唯一ID
      const uniqueId = `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      
      // 使用函数式更新来确保获取最新的状态
      setImages(prevImages => {
        // 检查是否已存在该图片
        const exists = prevImages.some(item => 
          item.name === file.name && 
          item.size === file.size
        );
        
        if (exists) {
          console.log(`图片已存在于列表中: ${file.name}`);
          return prevImages;
        }
        
        // 返回新列表
        const newImage = {
          uid: uniqueId,
          name: file.name,
          status: 'done',
          url: URL.createObjectURL(compressedFile),
          originFileObj: compressedFile,
          size: file.size
        };
        
        // 如果这是第一张图片，自动选择它作为模特生成图
        if (prevImages.length === 0) {
          setSelectedImage(newImage);
        }
        
        return [...prevImages, newImage];
      });
      
      // 阻止自动上传
      return false;
    } catch (error) {
      console.error('图片处理失败:', error);
      message.error('图片处理失败');
      return Upload.LIST_IGNORE;
    }
  };
  
  // 移除图片
  const handleRemoveImage = (file) => {
    const newFileList = images.filter(item => item.uid !== file.uid);
    setImages(newFileList);
    
    // 如果移除的是当前选中的图片，重置选中状态
    if (selectedImage && selectedImage.uid === file.uid) {
      setSelectedImage(newFileList.length > 0 ? newFileList[0] : null);
    }
  };
  
  // 选择用于生成模特图的图片
  const handleSelectImage = (file) => {
    setSelectedImage(file);
    message.success(`已选择图片「${file.name}」用于生成模特展示图`);
  };
  
  // 加载保存的提示词模板
  useEffect(() => {
    // 从本地存储加载模板
    const savedTemplates = localStorage.getItem('promptTemplates');
    if (savedTemplates) {
      try {
        setPromptTemplates(JSON.parse(savedTemplates));
      } catch (e) {
        console.error('加载提示词模板失败:', e);
        // 初始化空模板数组
        localStorage.setItem('promptTemplates', JSON.stringify([]));
      }
    } else {
      // 初始化默认模板
      const defaultTemplates = [
        {
          id: '1',
          name: '标准服装展示',
          content: '请将这件布料服装穿在模特身上，生成一张逼真的模特穿着图片。生成的图片应该保持模特的原姿势，然后正确地将布料服装穿在模特身上。请保持布料的原始样式、纹理和颜色，确保图片清晰、适合产品展示。'
        },
        {
          id: '2',
          name: '海边风格',
          content: '请将这件布料服装穿在模特身上，背景设置在美丽的海边，收取时尚唯美的效果。注意保持布料原本的色彩和纹理，对比度要适当。模特姿势自然实用。'
        }
      ];
      setPromptTemplates(defaultTemplates);
      localStorage.setItem('promptTemplates', JSON.stringify(defaultTemplates));
    }
  }, []);
  
  // 提示词模板相关操作已在上面定义

  // 删除提示词模板
  const deletePromptTemplate = (templateId) => {
    const updatedTemplates = promptTemplates.filter(t => t.id !== templateId);
    setPromptTemplates(updatedTemplates);
    localStorage.setItem('promptTemplates', JSON.stringify(updatedTemplates));
    message.success('提示词模板已删除');
  };
  
  // 调用Gemini API生成模特图
  const generateModelImage = async () => {
    if (!selectedImage) {
      message.error('请先选择一张样品图片');
      return;
    }
    
    // 清除选中的历史模特图，避免冲突
    setSelectedHistoryModel(null);
    // 清除九宫格图，让用户可以重新生成
    setNineGridImage(null);
    
    if (!customModelImage && !selectedModelTemplate) {
      message.error('请选择或上传一张模特图片');
      return;
    }
    
    setLoading(true);
    try {
      // 创建FormData对象
      const formData = new FormData();
      
      // 添加模特图片
      if (customModelImage) {
        formData.append('model', customModelImage.originFileObj);
      } else if (selectedModelTemplate) {
        try {
          // 从选择的模板获取File对象，注意这是本地图片路径
          const modelTemplateFile = await fetch(selectedModelTemplate.url, {
            headers: new Headers({
              'Origin': window.location.origin
            })
          })
          .then(res => {
            if (!res.ok) throw new Error(`获取模板图片失败: ${res.status}`);
            return res.blob();
          })
          .then(blob => new File([blob], 'model_template.jpg', { type: 'image/jpeg' }));
          
          formData.append('model', modelTemplateFile);
        } catch (error) {
          console.error('获取模板图片失败:', error);
          message.error('获取模板图片失败，请刷新页面或选择其他模板');
          setLoading(false);
          return;
        }
      }
      
      // 添加样品图片
      formData.append('fabric', selectedImage.originFileObj);
      
      // 添加提示词（使用选择的模板或自定义内容）
      const promptContent = currentPromptTemplate ? 
        promptTemplates.find(t => t.id === currentPromptTemplate)?.content || '' :
        customPrompt;
      
      if (promptContent) {
        formData.append('prompt', promptContent);
      }
      
      // 调用后端API - 直接使用生产环境地址
      // 获取存储的认证令牌
      const apiBaseUrl = getConfig().apiBaseUrl || 'https://warehouseapi.proxypool.eu.org';
      const token = localStorage.getItem('auth_token');

      const headers = {};
      if (token) {
        headers['X-Auth-Token'] = token;
      }
      
      const response = await axios.post(`${apiBaseUrl}/api/gemini/generate`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...headers
        }
      });
      
      // 处理响应，兼容多种响应格式
      console.log('服务器响应数据:', response.data);
      
      // 获取图片URL（处理不同的响应格式）
      const imageUrl = response.data?.imageUrl || response.data?.success?.imageUrl;
      
      if (imageUrl) {
        // 加载生成的模特图
        const modelImageObj = {
          uid: `model-${Date.now()}`,
          name: 'model-image.jpg',
          status: 'done',
          url: imageUrl,
          originalUrl: response.data?.originalUrl, // 保存原始地址以便调试
          isModelImage: true,
          createdAt: new Date().toISOString(),
          prompt: promptContent
        };
        
        // 保存到数据库
        try {
          const savedModel = await saveModelHistory({
            url: imageUrl,
            prompt: promptContent,
            metadata: {
              originalUrl: response.data?.originalUrl
            }
          });
          
          // 使用从数据库返回的ID
          modelImageObj.uid = savedModel.id;
          
          // 添加到模特历史记录
          setModelHistory(prev => [modelImageObj, ...prev]);
          
          // 清除之前选择的历史模特
          setSelectedHistoryModel(null);
          
          setModelImage(modelImageObj);
          message.success('模特展示图生成成功！');
          
          // 显示模特图预览
          setCurrentModelImage(imageUrl);
        } catch (error) {
          console.error('保存模特历史记录失败:', error);
          // 即使保存失败也不影响用户体验，仍然显示生成的图片
          setModelImage(modelImageObj);
          setCurrentModelImage(imageUrl);
        }
        setModelPreviewVisible(true);
        
        // 切换到水印选项卡
        setActiveTab('watermark');
        
        // 自动模式下，直接进行后续处理
        if (processMode === 'auto') {
          addWatermarkToAllImages();
        }
      } else if (response.data && (response.data.message || response.data.error)) {
        // 显示API返回的错误信息
        message.error('模特图生成失败: ' + (response.data.message || response.data.error || '请重试'));
      } else {
        message.error('模特图生成失败，请重试');
      }
    } catch (error) {
      console.error('调用Gemini API失败:', error);
      message.error('模特图生成失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  // 图片处理相关函数已迁移到 imageService.js
  
  // 为所有图片添加水印
  const addWatermarkToAllImages = async () => {
    // 使用选定的历史模特或当前模特
    const actualModelImg = selectedHistoryModel || modelImage;
    
    if (!actualModelImg) {
      message.error('请先生成模特展示图或从历史记录中选择模特图');
      return;
    }
    
    setLoading(true);
    try {
      // 处理所有样品图片
      const watermarkedImages = await Promise.all(
        images.map(async (img) => {
          const watermarkedUrl = await addWatermark(img.url, watermarkSettings);
          return {
            ...img,
            watermarkedUrl
          };
        })
      );
      
      // 处理模特图 - 使用选定的历史模特或当前模特
      const actualModelImg = selectedHistoryModel || modelImage;
      const watermarkedModelUrl = await addWatermark(actualModelImg.url, watermarkSettings);
      const watermarkedModel = {
        ...actualModelImg,
        watermarkedUrl: watermarkedModelUrl
      };
      
      // 更新状态
      setImages(watermarkedImages);
      
      // 根据使用的模特图来源更新相应的状态
      if (selectedHistoryModel) {
        setSelectedHistoryModel(watermarkedModel);
      } else {
        setModelImage(watermarkedModel);
      }
      
      message.success('水印添加成功！');
      
      // 自动模式下，继续合成九宫格
      if (processMode === 'auto') {
        // 使用带水印的图片生成九宫格
        createNineGridImage(watermarkedImages, watermarkedModel);
      }
    } catch (error) {
      console.error('添加水印失败:', error);
      message.error('添加水印失败: ' + (error.toString ? error.toString() : error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };
  
  // 合成九宫格图片
  const createNineGridImage = async (sampleImages, modelImg) => {
    // 使用选定的历史模特或当前模特
    const actualModelImg = selectedHistoryModel || modelImg;
    
    if (!actualModelImg || sampleImages.length === 0) {
      message.error('需要模特图和至少一张样品图才能生成九宫格');
      return;
    }
    
    setLoading(true);
    try {
      // 设置九宫格大小
      const gridSize = 600; // 每个格子的大小
      const gap = 10; // 格子间的间隔
      const canvasWidth = gridSize * 3 + gap * 2;
      const canvasHeight = gridSize * 3 + gap * 2;
      
      // 创建Canvas
      const canvas = createCanvas(canvasWidth, canvasHeight);
      const ctx = canvas.getContext('2d');
      
      // 填充背景
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      
      // 准备图片源 - 优先使用已添加水印的图片URL
      const imageUrls = sampleImages.map(img => img.watermarkedUrl || img.url);
      // 使用选中的历史模特图或当前模特图
      const modelToUse = selectedHistoryModel || actualModelImg;
      const modelImageUrl = modelToUse.watermarkedUrl || modelToUse.url;
      
      // 确保有足够的图片填充九宫格
      let imagesToUse = [...imageUrls];
      while (imagesToUse.length < 8) {
        // 如果图片不足，重复使用现有图片
        imagesToUse = [...imagesToUse, ...imageUrls];
      }
      // 只取前8张
      imagesToUse = imagesToUse.slice(0, 8);
      
      // 加载所有图片
      const loadedImages = await Promise.all(
        [...imagesToUse.map(url => loadImage(url)), loadImage(modelImageUrl)]
      );
      
      // 分离模特图和样品图
      const modelImageElement = loadedImages[loadedImages.length - 1];
      const sampleImageElements = loadedImages.slice(0, loadedImages.length - 1);
      
      // 绘制九宫格
      let index = 0;
      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 3; col++) {
          const x = col * (gridSize + gap);
          const y = row * (gridSize + gap);
          
          // 中心位置放模特图
          let imgToDraw;
          if (row === 1 && col === 1) {
            imgToDraw = modelImageElement;
          } else {
            imgToDraw = sampleImageElements[index++];
          }
          
          // 绘制图片并保持比例
          drawImageFit(ctx, imgToDraw, x, y, gridSize, gridSize);
          
          // 绘制格子边框
          ctx.strokeStyle = '#eaeaea';
          ctx.lineWidth = 1;
          ctx.strokeRect(x, y, gridSize, gridSize);
        }
      }
      
      // 生成九宫格图片URL
      const nineGridUrl = canvas.toDataURL('image/jpeg', 0.9);
      setNineGridImage(nineGridUrl);
      
      message.success('九宫格生成成功！');
      
      // 自动给九宫格图添加水印
      if (watermarkSettings.enabled) {
        const watermarkedNineGridUrl = await addWatermark(nineGridUrl, watermarkSettings);
        setNineGridImage(watermarkedNineGridUrl);
      }
    } catch (error) {
      console.error('合成九宫格失败:', error);
      message.error('合成九宫格失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // 辅助函数-保持图片比例适应容器
  const drawImageFit = (ctx, img, x, y, width, height) => {
    const scale = Math.min(width / img.width, height / img.height);
    const newWidth = img.width * scale;
    const newHeight = img.height * scale;
    const offsetX = (width - newWidth) / 2;
    const offsetY = (height - newHeight) / 2;
    
    ctx.drawImage(img, x + offsetX, y + offsetY, newWidth, newHeight);
  };
  
  // 下载图片
  const downloadImage = (imageUrl, filename) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // 切换处理模式
  const handleModeChange = (mode) => {
    setProcessMode(mode);
  };
  
  // 切换标签页
  const handleTabChange = (key) => {
    setActiveTab(key);
  };
  
  // 更新水印设置
  const updateWatermarkSetting = (key, value) => {
    setWatermarkSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  return (
    <div className="sample-processor-container" style={{ padding: '20px' }}>
      <Card
        title={<Title level={3}>样品图片处理系统</Title>}
        extra={
          <Radio.Group
            value={processMode}
            onChange={(e) => handleModeChange(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="auto">自动模式</Radio.Button>
            <Radio.Button value="manual">手动模式</Radio.Button>
          </Radio.Group>
        }
      >
        <Spin spinning={loading} tip="处理中...">
          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            {/* 图片上传标签页 */}
            <TabPane tab="图片上传" key="upload">
              <div style={{ marginBottom: 20 }}>
                <Text type="secondary">
                  请上传最多8张样品图片，系统将自动生成模特展示图并合成九宫格。
                  {processMode === 'auto' ? '自动模式将自动完成所有步骤。' : '手动模式允许你手动控制每一步。'}
                </Text>
              </div>
              
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card title="样品图片" bordered={false}>
                    <Upload
                      ref={uploadRef}
                      listType="picture-card"
                      fileList={images}
                      beforeUpload={handleImageUpload}
                      onRemove={handleRemoveImage}
                      multiple
                      accept="image/*"
                    >
                      {images.length >= 8 ? null : (
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 8 }}>上传样品图</div>
                        </div>
                      )}
                    </Upload>
                    
                    <div style={{ marginTop: 16 }}>
                      <Text strong>已上传 {images.length}/8 张样品图片</Text>
                    </div>
                  </Card>
                </Col>
                
                <Col span={12}>
                  <Card bordered={false}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <Typography.Title level={5} style={{ margin: 0 }}>模特图片</Typography.Title>
                      <Button 
                        type="primary" 
                        ghost 
                        size="small" 
                        icon={<ExperimentOutlined />}
                        onClick={() => setModelHistoryVisible(true)}
                        disabled={modelHistory.length === 0}
                      >
                        历史模特
                      </Button>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                      <div>
                        <Upload
                          listType="picture-card"
                          showUploadList={false}
                          beforeUpload={handleModelImageUpload}
                          accept="image/*"
                          style={{ marginBottom: 0 }}
                        >
                          <div style={{ 
                            padding: '8px', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: '#666'
                          }}>
                            <UserOutlined style={{ fontSize: '22px', marginBottom: '8px', color: '#40a9ff' }} />
                            <div>上传模特</div>
                          </div>
                        </Upload>
                        <div style={{ fontSize: '12px', color: '#888', textAlign: 'center', marginTop: '4px' }}>自定义模特</div>
                      </div>
                      
                      {customModelImage && (
                        <div style={{ 
                          flex: 1,
                          padding: '12px',
                          background: 'rgba(82, 196, 26, 0.1)',
                          borderRadius: '8px',
                          border: '1px solid rgba(82, 196, 26, 0.3)'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div style={{ position: 'relative', marginRight: '12px' }}>
                                <img 
                                  src={customModelImage.url}
                                  alt="自定义模特"
                                  style={{ width: '60px', height: '80px', objectFit: 'cover', borderRadius: '4px' }}
                                  onError={handleImageError}
                                />
                                <div style={{ 
                                  position: 'absolute', 
                                  top: '4px', 
                                  right: '4px', 
                                  background: 'rgba(82, 196, 26, 0.8)', 
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '18px',
                                  height: '18px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <CheckOutlined style={{ fontSize: '12px' }} />
                                </div>
                              </div>
                              <div>
                                <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>自定义模特图</div>
                                <div style={{ color: '#666', fontSize: '12px' }}>已选择使用自定义模特图片</div>
                              </div>
                            </div>
                            <Button 
                              size="small" 
                              danger 
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                setCustomModelImage(null);
                                message.success('已移除自定义模特图');
                              }}
                            >
                              移除
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <Typography.Title level={5} style={{ margin: 0 }}>选择模特</Typography.Title>
                      <Space>
                        <Select
                          placeholder="人种筛选"
                          style={{ width: 100 }}
                          value={modelCategoryFilter}
                          onChange={(value) => setModelCategoryFilter(value)}
                          size="small"
                          options={[
                            { value: 'all', label: '全部人种' },
                            { value: '亚洲人', label: '亚洲人' },
                            { value: '欧洲人', label: '欧洲人' },
                            { value: '非洲人', label: '非洲人' }
                          ]}
                        />
                        <Select
                          placeholder="年龄段筛选"
                          style={{ width: 100 }}
                          value={modelAgeFilter}
                          onChange={(value) => setModelAgeFilter(value)}
                          size="small"
                          options={[
                            { value: 'all', label: '全部年龄' },
                            { value: '青年', label: '青年' },
                            { value: '中年', label: '中年' },
                            { value: '老年', label: '老年' }
                          ]}
                        />
                      </Space>
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', 
                      gap: '8px',
                      maxHeight: '240px',
                      overflowY: 'auto',
                      padding: '4px'
                    }}>
                      {modelTemplates
                        .filter(template => {
                          if (modelCategoryFilter !== 'all' && template.category !== modelCategoryFilter) return false;
                          if (modelAgeFilter !== 'all' && template.ageGroup !== modelAgeFilter) return false;
                          return true;
                        })
                        .map(template => (
                        <div 
                          key={template.id}
                          style={{
                            borderRadius: '4px',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: selectedModelTemplate && selectedModelTemplate.id === template.id ? 
                              '0 0 0 2px #1890ff' : '0 1px 2px rgba(0,0,0,0.15)',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => handleSelectModelTemplate(template)}
                        >
                          <img 
                            src={template.url}
                            alt={template.name}
                            style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover' }}
                            onError={handleImageError}
                          />
                          {selectedModelTemplate && selectedModelTemplate.id === template.id && (
                            <div style={{ 
                              position: 'absolute', 
                              top: '4px', 
                              right: '4px', 
                              background: 'rgba(24, 144, 255, 0.8)', 
                              color: 'white',
                              borderRadius: '50%',
                              width: '18px',
                              height: '18px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px'
                            }}>
                              <CheckOutlined />
                            </div>
                          )}
                          <Tooltip title={`${template.name} (${template.category}, ${template.ageGroup})`}>
                            <div style={{ 
                              fontSize: '10px', 
                              padding: '2px 4px', 
                              textAlign: 'center',
                              background: 'rgba(0,0,0,0.03)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {template.name}
                            </div>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
              </Row>
              
              <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
                <Col span={24}>
                  <Card title="提示词设置" bordered={false}>
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form layout="vertical">
                          <Form.Item label="选择提示词模板">
                            <Select
                              placeholder="选择提示词模板"
                              style={{ width: '100%' }}
                              value={currentPromptTemplate || undefined}
                              onChange={handleSelectPromptTemplate}
                              allowClear
                              options={promptTemplates.map(template => ({
                                key: template.id,
                                value: template.id,
                                label: template.name
                              }))}
                            />
                          </Form.Item>
                          
                          <Form.Item label="自定义提示词">
                            <Input.TextArea 
                              rows={4} 
                              value={customPrompt}
                              onChange={e => setCustomPrompt(e.target.value)}
                              placeholder="输入自定义提示词，用于指导 AI 生成模特穿着图像..."
                            />
                          </Form.Item>
                        </Form>
                      </Col>
                      
                      <Col span={12}>
                        <Form layout="vertical">
                          <Form.Item label="添加新模板">
                            <Input placeholder="模板名称" id="new-template-name" style={{ marginBottom: '8px' }} />
                            <Input.TextArea 
                              rows={3} 
                              placeholder="模板内容" 
                              id="new-template-content" 
                              style={{ marginBottom: '8px' }}
                            />
                            <Button 
                              type="primary"
                              onClick={() => {
                                const nameEl = document.getElementById('new-template-name');
                                const contentEl = document.getElementById('new-template-content');
                                const name = nameEl.value.trim();
                                const content = contentEl.value.trim();
                                
                                if (name && content) {
                                  savePromptTemplate(name, content);
                                  nameEl.value = '';
                                  contentEl.value = '';
                                } else {
                                  message.error('请填写模板名称和内容');
                                }
                              }}
                            >
                              保存模板
                            </Button>
                          </Form.Item>
                        </Form>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
              
              <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
                <Col span={24}>
                  <Card bordered={false}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <Typography.Title level={5} style={{ margin: 0 }}>样品图片管理</Typography.Title>
                      <div>
                        <Text strong type="secondary" style={{ marginRight: '8px' }}>已上传: </Text>
                        <Text strong style={{ color: images.length >= 8 ? '#ff4d4f' : (images.length > 0 ? '#1890ff' : '#595959') }}>
                          {images.length}/8
                        </Text>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                      <Button 
                        type="primary" 
                        icon={<ExperimentOutlined />} 
                        onClick={generateModelImage}
                        disabled={!selectedImage || (!customModelImage && !selectedModelTemplate)}
                        style={{ flex: 1 }}
                      >
                        生成模特展示图
                      </Button>
                      
                      {modelImage && (
                        <Button 
                          icon={<EyeOutlined />}
                          onClick={() => {
                            setCurrentModelImage(modelImage.url);
                            setModelPreviewVisible(true);
                          }}
                        >
                          查看当前模特
                        </Button>
                      )}
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                      {images.map((img, index) => (
                        <div 
                          key={img.uid} 
                          style={{
                            borderRadius: '6px',
                            overflow: 'hidden',
                            boxShadow: selectedImage && selectedImage.uid === img.uid ? 
                              '0 0 0 2px #1890ff, 0 4px 8px rgba(0,0,0,0.1)' : '0 2px 6px rgba(0,0,0,0.08)',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            background: '#fff'
                          }}
                          onClick={() => setSelectedImage(img)}
                        >
                          <div style={{ position: 'relative' }}>
                            <img 
                              src={img.url} 
                              alt={`样品图 ${index + 1}`}
                              style={{ 
                                width: '100%', 
                                height: '160px', 
                                objectFit: 'cover'
                              }}
                              onError={handleImageError}
                            />
                            {selectedImage && selectedImage.uid === img.uid && (
                              <div style={{ 
                                position: 'absolute', 
                                top: '8px', 
                                right: '8px', 
                                background: 'rgba(24, 144, 255, 0.9)', 
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <CheckOutlined />
                              </div>
                            )}
                            <div style={{
                              position: 'absolute',
                              bottom: '0',
                              right: '0',
                              padding: '2px 8px',
                              background: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              fontSize: '12px',
                              borderTopLeftRadius: '4px'
                            }}>
                              {index + 1}/8
                            </div>
                          </div>
                          
                          <div style={{ padding: '8px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              样品图 {index + 1}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ fontSize: '11px', color: '#888', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '100px' }}>
                                {img.name}
                              </div>
                              <Button 
                                danger 
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveImage(img);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
              </Row>
            </TabPane>
            
            {/* 水印设置标签页 */}
            <TabPane tab="水印设置" key="watermark">
              <Form layout="vertical">
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item label="启用水印">
                      <Switch 
                        checked={watermarkSettings.enabled} 
                        onChange={(checked) => updateWatermarkSetting('enabled', checked)} 
                      />
                    </Form.Item>
                  </Col>
                  
                  {watermarkSettings.enabled && (
                    <>
                      <Col span={12}>
                        <Form.Item label="水印文字">
                          <Input 
                            value={watermarkSettings.text} 
                            onChange={(e) => updateWatermarkSetting('text', e.target.value)} 
                            placeholder="输入水印文字"
                          />
                        </Form.Item>
                      </Col>
                      
                      <Col span={12}>
                        <Form.Item label="水印位置">
                          <Select
                            value={watermarkSettings.position}
                            onChange={(value) => updateWatermarkSetting('position', value)}
                            options={[
                              { value: 'center', label: '中心' },
                              { value: 'topLeft', label: '左上角' },
                              { value: 'topRight', label: '右上角' },
                              { value: 'bottomLeft', label: '左下角' },
                              { value: 'bottomRight', label: '右下角' }
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      
                      <Col span={12}>
                        <Form.Item label="文字大小">
                          <Slider
                            min={12}
                            max={72}
                            value={watermarkSettings.fontSize}
                            onChange={(value) => updateWatermarkSetting('fontSize', value)}
                          />
                        </Form.Item>
                      </Col>
                      
                      <Col span={12}>
                        <Form.Item label="透明度">
                          <Slider
                            min={0.1}
                            max={1}
                            step={0.1}
                            value={watermarkSettings.opacity}
                            onChange={(value) => updateWatermarkSetting('opacity', value)}
                          />
                        </Form.Item>
                      </Col>
                      
                      <Col span={12}>
                        <Form.Item label="旋转角度">
                          <Slider
                            min={-90}
                            max={90}
                            value={watermarkSettings.angle}
                            onChange={(value) => updateWatermarkSetting('angle', value)}
                          />
                        </Form.Item>
                      </Col>
                      
                      <Col span={12}>
                        <Form.Item label="重复水印">
                          <Switch 
                            checked={watermarkSettings.repeat} 
                            onChange={(checked) => updateWatermarkSetting('repeat', checked)} 
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
                
                {modelImage && (
                  <Button 
                    type="primary" 
                    onClick={addWatermarkToAllImages}
                    icon={<EditOutlined />}
                  >
                    添加水印
                  </Button>
                )}
              </Form>
            </TabPane>
            
            {/* 九宫格生成标签页 */}
            <TabPane tab="九宫格生成" key="ninegrid">
              {!modelImage && !selectedHistoryModel ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px', 
                  background: '#f7f7f7', 
                  border: '1px dashed #d9d9d9',
                  borderRadius: '8px',
                  color: '#595959',
                  margin: '20px 0'
                }}>
                  <PictureOutlined style={{ fontSize: '32px', color: '#bfbfbf', marginBottom: '12px', display: 'block' }} />
                  <Text type="secondary" style={{ fontSize: '14px' }}>请先生成模特展示图，再进行九宫格生成</Text>
                </div>
              ) : (
                <div>
                  {nineGridImage ? (
                    <div style={{ textAlign: 'center' }}>
                      <img 
                        src={nineGridImage} 
                        alt="九宫格图片" 
                        style={{ maxWidth: '100%', border: '1px solid #eaeaea' }} 
                        onError={handleImageError}
                      />
                      
                      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        <Button 
                          type="primary" 
                          icon={<SaveOutlined />} 
                          onClick={() => downloadImage(nineGridImage, `九宫格_${Date.now()}.jpg`)}
                        >
                          下载九宫格图片
                        </Button>
                        <Button 
                          type="primary" 
                          icon={<AppstoreAddOutlined />} 
                          onClick={() => {
                            setPatternFormData({
                              name: '',
                              categoryId: patternCategories.length > 0 ? patternCategories[0].id : '',
                              description: '由样品处理系统生成的九宫格图片'
                            });
                            setAddToPatternVisible(true);
                          }}
                        >
                          添加到样板系统
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 20 }}>
                      <Button 
                        type="primary" 
                        icon={<PictureOutlined />} 
                        onClick={() => createNineGridImage(images, modelImage)}
                        disabled={!modelImage && !selectedHistoryModel}
                      >
                        生成九宫格
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </TabPane>
          </Tabs>
        </Spin>
      </Card>
      
      {/* 模特预览对话框 */}
      <Modal
        title="模特展示图预览"
        open={modelPreviewVisible}
        onCancel={() => setModelPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModelPreviewVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="regenerate" 
            type="primary" 
            onClick={() => {
              setModelPreviewVisible(false);
              generateModelImage();
            }}
          >
            重新生成
          </Button>
        ]}
        width={800}
      >
        {currentModelImage && (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={currentModelImage}
              alt="模特展示图"
              style={{ maxWidth: '100%', maxHeight: '60vh' }}
              crossOrigin="anonymous"
              fallback={fallbackImageUrl}
              placeholder={
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '300px', background: '#f5f5f5' }}>
                  <UserOutlined style={{ fontSize: '48px', color: '#bfbfbf' }} />
                </div>
              }
            />
            <p style={{ marginTop: '15px' }}>
              如果结果不满意，可以调整提示词或选择不同的模板重新生成。
            </p>
          </div>
        )}
      </Modal>
      
      {/* 添加到样板系统对话框 */}
      <Modal
        title="添加到样板系统"
        open={addToPatternVisible}
        onCancel={() => setAddToPatternVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setAddToPatternVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={loading}
            onClick={async () => {
              try {
                if (!patternFormData.name.trim()) {
                  message.error('样板名称不能为空');
                  return;
                }

                if (!patternFormData.categoryId) {
                  message.error('请选择样板分类');
                  return;
                }

                setLoading(true);
                
                // 将九宫格图片转换为 Blob 对象
                const response = await fetch(nineGridImage);
                const blob = await response.blob();
                
                // 创建 File 对象
                const file = new File([blob], `九宫格_${Date.now()}.jpg`, { type: 'image/jpeg' });
                
                // 转换为 base64
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onloadend = async () => {
                  const base64data = reader.result;
                  
                  // 准备提交数据
                  const submitData = {
                    ...patternFormData,
                    imageData: base64data
                  };
                  
                  // 调用 API 创建样板
                  const result = await createPattern(submitData);
                  
                  if (result.success) {
                    message.success('样板添加成功！');
                    setAddToPatternVisible(false);
                  } else {
                    message.error(result.message || '添加样板失败');
                  }
                };
              } catch (error) {
                console.error('添加样板失败:', error);
                message.error('添加样板失败: ' + (error.message || '未知错误'));
              } finally {
                setLoading(false);
              }
            }}
          >
            保存
          </Button>
        ]}
      >
        <Form layout="vertical">
          <Form.Item label="样板名称" required>
            <Input 
              name="name" 
              value={patternFormData.name} 
              onChange={handlePatternInputChange} 
              placeholder="请输入样板名称"
            />
          </Form.Item>
          
          <Form.Item label="样板分类" required>
            <Select
              name="categoryId"
              value={patternFormData.categoryId}
              onChange={(value) => setPatternFormData({...patternFormData, categoryId: value})}
              placeholder="请选择样板分类"
              style={{ width: '100%' }}
            >
              {patternCategories.map(category => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item label="样板描述">
            <Input.TextArea 
              name="description" 
              value={patternFormData.description} 
              onChange={handlePatternInputChange} 
              placeholder="请输入样板描述"
              rows={4}
            />
          </Form.Item>
          
          <div style={{ marginTop: '16px' }}>
            <Typography.Text type="secondary">
              图片预览：
            </Typography.Text>
            <div style={{ marginTop: '8px', border: '1px solid #eaeaea', padding: '8px', textAlign: 'center' }}>
              <img 
                src={nineGridImage} 
                alt="样板图片预览" 
                style={{ maxWidth: '100%', maxHeight: '200px' }} 
                onError={handleImageError}
              />
            </div>
          </div>
        </Form>
      </Modal>

      {/* 模特历史记录对话框 */}
      <Modal
        title="模特历史记录"
        open={modelHistoryVisible}
        onCancel={() => {
          setModelHistoryVisible(false);
        }}
        footer={[
          <Button key="refresh" onClick={loadModelHistory} loading={loadingHistory}>
            刷新
          </Button>,
          <Button key="close" onClick={() => setModelHistoryVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="use" 
            type="primary" 
            disabled={!selectedHistoryModel}
            onClick={() => {
              setModelHistoryVisible(false);
              // 清除当前模特图，避免冲突
              setModelImage(null);
              // 清除九宫格图，让用户可以重新生成
              setNineGridImage(null);
              message.success('已选择历史模特图，可继续生成九宫格');
            }}
          >
            使用选中模特
          </Button>
        ]}
        width={800}
      >
        {loadingHistory ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin tip="加载中..."/>
          </div>
        ) : modelHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Text type="secondary">暂无模特历史记录</Text>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">点击选择要使用的历史模特图，然后点击“使用选中模特”按钮</Text>
            </div>
            <Row gutter={[16, 16]}>
              {modelHistory.map(model => (
                <Col span={8} key={model.uid}>
                  <Card
                    hoverable
                    cover={
                      <div 
                        style={{ 
                          height: 200, 
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: selectedHistoryModel?.uid === model.uid ? '2px solid #1890ff' : 'none'
                        }}
                      >
                        <img 
                          alt="model" 
                          src={model.url} 
                          style={{ width: '100%', objectFit: 'cover' }}
                          onClick={() => setSelectedHistoryModel(model)}
                          onError={handleImageError}
                        />
                      </div>
                    }
                    actions={[
                      <DeleteOutlined 
                        key="delete" 
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await deleteModelHistory(model.uid);
                            message.success('删除成功');
                            // 如果删除的是当前选中的模特，清除选中状态
                            if (selectedHistoryModel?.uid === model.uid) {
                              setSelectedHistoryModel(null);
                            }
                            // 重新加载历史记录
                            loadModelHistory();
                          } catch (error) {
                            message.error('删除失败');
                          }
                        }}
                      />
                    ]}
                  >
                    <Card.Meta
                      title={`模特 ${new Date(model.createdAt).toLocaleString()}`}
                      description={
                        <div style={{ height: 40, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {model.prompt || '无提示词'}
                        </div>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SampleProcessor;
